import { resumeAcces } from '../../../../../web-commun/connexion-whatsapp/api'
import { afficherTelephone } from '../../../../supabase/functions/_shared/otp-whatsapp'

/**
 * Résultat des edge functions de création de compte (`create-merchant-account`,
 * `create-driver-account`, `create-merchant-staff`, `invite-member`).
 *
 * Quand l'e-mail avait DÉJÀ un compte confirmé, la fonction ne touche pas à son
 * mot de passe : elle répond `temp_password: null` et `compte_existant: true`.
 * Il ne faut alors jamais afficher « null » : on dit à la personne comment se
 * connecter.
 */
export const MESSAGE_COMPTE_EXISTANT_FONCTION =
  'Ce compte existait déjà : la personne se connecte avec son mot de passe habituel ou « Continuer avec Google ».'

export function aMotDePasseTemporaire(
  r: { temp_password?: string | null | undefined } | null | undefined,
): r is { temp_password: string } {
  return typeof r?.temp_password === 'string' && r.temp_password.length > 0
}

/* ─────────────────────────────────────────────────────────────────────────
   Comptes créés avec un e-mail OU un numéro WhatsApp (au moins un)
   ───────────────────────────────────────────────────────────────────────── */

/** Réponse commune des quatre edge functions de création de compte. */
export interface ReponseCreation {
  email?: string | null
  /** E.164 (« +22370123456 »). */
  phone?: string | null
  temp_password?: string | null
  compte_existant?: boolean
  connexion?: 'mot_de_passe' | 'whatsapp' | 'existant' | string | null
}

/** Ce que l'écran « accès à remettre » affiche après une création. */
export interface AccesARemettre {
  /** `mot_de_passe` : e-mail + mot de passe temporaire ; `whatsapp` : numéro seul ; `existant` : compte déjà là. */
  mode: 'mot_de_passe' | 'whatsapp' | 'existant'
  email: string | null
  /** Numéro mis en forme pour l'affichage (« +223 70 12 34 56 »), ou null. */
  telephone: string | null
  motDePasse: string | null
  /** Phrase à afficher sous les identifiants (vide quand un mot de passe temporaire est remis). */
  phrase: string
}

/**
 * Décide quoi remettre à la personne. Avec un mot de passe temporaire : e-mail + mot de passe, comme avant.
 * Avec un numéro seul : PAS de mot de passe, la phrase de `resumeAcces` (« La personne se connecte avec son numéro
 * WhatsApp »). Compte déjà existant : on ne touche à rien et on le dit (jamais « null » à l'écran).
 */
export function accesARemettre(r: ReponseCreation | null | undefined): AccesARemettre {
  const email = (r?.email ?? '').trim() || null
  const phone = (r?.phone ?? '').trim() || null
  const telephone = phone ? afficherTelephone(phone) || phone : null
  if (aMotDePasseTemporaire(r)) {
    return { mode: 'mot_de_passe', email, telephone, motDePasse: r.temp_password, phrase: '' }
  }
  if (r?.connexion === 'whatsapp' || (!email && phone)) {
    return { mode: 'whatsapp', email, telephone, motDePasse: null, phrase: resumeAcces({ ...r, email, phone }).phrase }
  }
  return { mode: 'existant', email, telephone, motDePasse: null, phrase: MESSAGE_COMPTE_EXISTANT_FONCTION }
}

/**
 * Traductions des refus des edge functions de création de compte (e-mail ou numéro). `invalid_phone` n'y figure
 * pas : le serveur joint déjà la phrase française exacte dans `detail` (voir `CODES_DETAIL_PRIORITAIRE`).
 */
export const LISIBLE_COMPTES: Record<string, string> = {
  compte_autre_role:
    "Cette adresse ou ce numéro est déjà utilisé par un compte d'un autre type (équipe, livreur ou marchand). Utilisez une autre adresse ou un autre numéro.",
  comptes_multiples: 'Plusieurs comptes ont cette adresse ou ce numéro : contactez le support.',
  phone_already_used: 'Ce numéro est déjà utilisé par un autre compte.',
  identifiants_differents:
    "Cet e-mail et ce numéro appartiennent à deux comptes différents. Corrigez l'un des deux, ou n'en renseignez qu'un.",
}

/** Codes dont le `detail` du serveur est déjà la bonne phrase française (plus précise que toute phrase fixe). */
export const CODES_DETAIL_PRIORITAIRE: ReadonlySet<string> = new Set(['invalid_phone'])

export function messageErreurFonction(
  code: string,
  detail: string,
  table: Record<string, string>,
  repli: string,
  prioritaires: ReadonlySet<string> = CODES_DETAIL_PRIORITAIRE,
): string {
  if (detail && prioritaires.has(code)) return detail
  return table[code] || detail || code || repli
}
